import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import { getCountryById, patchCountry } from "../services/countryService";
import { toast } from "../utils/toast";
import { useParams } from "react-router-dom";
import { resolveRoleName } from "../utils/auth";

const CountryEditPage = () => {
  const { setActiveSection } = useAppContext();
  const { countryId: countryIdParam } = useParams();

  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  const [form, setForm] = useState({
    countryName: "",
    isoCode2: "",
    isoCode3: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const handleIso2Change = (value) => {
    setField("isoCode2", String(value || "").toUpperCase());
  };

  const handleIso3Change = (value) => {
    setField("isoCode3", String(value || "").toUpperCase());
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let idParam = countryIdParam;
        if (!idParam) {
          try {
            idParam = localStorage.getItem("activeCountryId") || "";
          } catch {}
        }
        const id = Number(idParam);
        if (!Number.isFinite(id) || id <= 0) {
          toast.error("No country selected");
          setActiveSection("countries");
          return;
        }
        const data = await getCountryById(id);
        setForm({
          countryName: data?.countryName || "",
          isoCode2: data?.isoCode2 || "",
          isoCode3: data?.isoCode3 || "",
        });
      } catch (e) {
        toast.error(e?.message || "Failed to load country");
      } finally {
        setLoading(false);
      }
    })();
  }, [countryIdParam, setActiveSection]);

  const errors = useMemo(() => {
    const e = {};
    if (!String(form.countryName).trim())
      e.countryName = "Country name is required";
    if (!String(form.isoCode2).trim()) e.isoCode2 = "ISO Code 2 is required";
    if (!String(form.isoCode3).trim()) e.isoCode3 = "ISO Code 3 is required";
    if (
      String(form.isoCode2).trim() &&
      String(form.isoCode2).trim().length !== 2
    ) {
      e.isoCode2 = "ISO Code 2 must be exactly 2 characters";
    }
    if (
      String(form.isoCode3).trim() &&
      String(form.isoCode3).trim().length !== 3
    ) {
      e.isoCode3 = "ISO Code 3 must be exactly 3 characters";
    }
    return e;
  }, [form]);

  const canSave = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const onSave = async () => {
    if (!canSave || saving) return;
    try {
      setSaving(true);
      let idParam = countryIdParam;
      if (!idParam) {
        try {
          idParam = localStorage.getItem("activeCountryId") || "";
        } catch {}
      }
      const id = Number(idParam);
      if (!Number.isFinite(id) || id <= 0) {
        toast.error("No country selected");
        return;
      }
      await patchCountry(id, {
        countryName: form.countryName.trim(),
        isoCode2: form.isoCode2.trim().toUpperCase(),
        isoCode3: form.isoCode3.trim().toUpperCase(),
      });
      toast.success("Country updated successfully!");
      setActiveSection("countries");
    } catch (e) {
      toast.error(e?.message || "Failed to update country");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => setActiveSection("countries");

  return (
    <div className="page-container">
      {!roleChecked ?
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      : !canAccess ?
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      : <>
          <div className="page-header">
            <div className="header-content">
              <div>
                <h2 className="page-title">Edit Country</h2>
                <p className="page-subtitle">
                  {form.countryName ?
                    `Editing: ${form.countryName}`
                  : "Update country details"}
                  {form.isoCode2 ? ` (${form.isoCode2})` : ""}
                </p>
              </div>
              <div className="roles-toolbar">
                <button className="secondary-btn" onClick={onCancel}>
                  Cancel
                </button>
                <button
                  className="primary-btn"
                  onClick={onSave}
                  disabled={!canSave || saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>

          <div className="page-content">
            {loading ?
              <div className="no-results">Loading...</div>
            : <div className="role-card">
                <div
                  className="section-title"
                  style={{ marginBottom: 12, fontWeight: 600 }}
                >
                  Basic Details
                </div>
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Country Name *</label>
                    <input
                      value={form.countryName}
                      onChange={(e) => setField("countryName", e.target.value)}
                      placeholder="e.g. United States"
                    />
                    {errors.countryName && (
                      <div
                        className="input-hint"
                        style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.countryName}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>ISO Code 2 *</label>
                    <input
                      value={form.isoCode2}
                      onChange={(e) => handleIso2Change(e.target.value)}
                      placeholder="e.g. US"
                      maxLength={2}
                    />
                    <div
                      className="input-hint"
                      style={{ fontSize: 12, marginTop: 4, color: "var(--gray-500)" }}
                    >
                      Exactly 2 characters.
                    </div>
                    {errors.isoCode2 && (
                      <div
                        className="input-hint"
                        style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.isoCode2}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>ISO Code 3 *</label>
                    <input
                      value={form.isoCode3}
                      onChange={(e) => handleIso3Change(e.target.value)}
                      placeholder="e.g. USA"
                      maxLength={3}
                    />
                    <div
                      className="input-hint"
                      style={{ fontSize: 12, marginTop: 4, color: "var(--gray-500)" }}
                    >
                      Exactly 3 characters.
                    </div>
                    {errors.isoCode3 && (
                      <div
                        className="input-hint"
                        style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.isoCode3}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            }
          </div>
        </>
      }
    </div>
  );
};

export default CountryEditPage;
